import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { PortfolioPosition } from '@/api/fund-views/queries';
import { SortDirection, SortDirectionType } from '@/api/types';
import ArrowRightIcon from '@/assets/svg/ui-elements/arrow-right-outline.svg';
import ArrowUpRightIcon from '@/assets/svg/ui-elements/arrow-up-right.svg';
import CoinsIcon from '@/assets/svg/ui-elements/coins.svg';
import ShieldIcon from '@/assets/svg/ui-elements/shield-outline.svg';
import {
  Badge,
  getTableMinWidth,
  IconButton,
  TableColumn,
  TableRow,
  TextCell,
  Tooltip,
} from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { MobileCardField, MobileFieldsGrid } from '@/shared/design-system';
import { formatDefaultFractionTwo } from '@/utils/numbers/formatters';

import {
  AssetNameCell,
  MobileRowActions,
  RowActions,
  SelectableTable,
} from './assets-table.styled';
import { MobileCardList } from './my-assets/mobile-card-list';
import { PerformancePositionSelectionIcon } from './performance-position-selection-icon';

export const actions = [
  { name: 'Collateralize', icon: <ShieldIcon /> },
  { name: 'Sell', icon: <ArrowUpRightIcon /> },
  { name: 'Redeem', icon: <CoinsIcon /> },
];

const SORT_KEYS = ['assetName', 'amount', 'costBasis', 'presentValue'] as const;
type SortKey = (typeof SORT_KEYS)[number];

interface AssetRow extends TableRow {
  position: PortfolioPosition;
}

const formatUsd = (value: number) => `$${formatDefaultFractionTwo(value)}`;

const getAssetVisibility = (row: PortfolioPosition) =>
  row.asset.isApproved ? 'Public' : 'Private';

const VisibilityBadge = ({ row }: { row: PortfolioPosition }) => {
  const visibility = getAssetVisibility(row);

  return (
    <Badge
      label={visibility}
      color={visibility === 'Public' ? 'primary' : 'default'}
      variant="subtle"
    />
  );
};

const AssetActions = ({ row }: { row: PortfolioPosition }) => (
  <>
    {actions.map((action) => (
      <Tooltip title={action.name} key={action.name} side="top">
        <span>
          <IconButton
            variant="outline"
            color="tertiary"
            size="sm"
            disabled
            aria-label={action.name}
          >
            {action.icon}
          </IconButton>
        </span>
      </Tooltip>
    ))}
    <Tooltip title="View" side="top">
      <span>
        <IconButton
          variant="outline"
          color="tertiary"
          size="sm"
          aria-label={`View ${row.asset.name}`}
          disabled={!row.asset.isApproved}
          onClick={(event) => event.stopPropagation()}
          {...(row.asset.isApproved && {
            nativeButton: false,
            render: <RouterLink to={`/all-funds/${row.asset.slug}`} />,
          })}
        >
          <ArrowRightIcon />
        </IconButton>
      </span>
    </Tooltip>
  </>
);

const compareBy = (key: SortKey, a: PortfolioPosition, b: PortfolioPosition): number => {
  switch (key) {
    case 'assetName':
      return String(a.asset.name).localeCompare(String(b.asset.name));
    case 'amount':
      return +a.amount - +b.amount;
    case 'costBasis':
      return (a.costBasis || 0) - (b.costBasis || 0);
    case 'presentValue':
      return (a.valueInUsd || 0) - (b.valueInUsd || 0);
  }
};

const AssetTable = ({
  activeAsset,
  setActiveAsset,
  data,
}: {
  activeAsset: PortfolioPosition | null;
  setActiveAsset: (_asset: PortfolioPosition | null) => void;
  data: PortfolioPosition[];
}) => {
  const isMobile = useIsMobile();
  const [sortKey, setSortKey] = useState<SortKey>('assetName');
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.ASC);

  const handleSort = (key: string) => {
    if (!SORT_KEYS.includes(key as SortKey)) return;
    if (key === sortKey) {
      setSortDirection(
        sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC,
      );
      return;
    }
    setSortKey(key as SortKey);
    setSortDirection(SortDirection.ASC);
  };

  const sortedData = useMemo(() => {
    const sign = sortDirection === SortDirection.ASC ? 1 : -1;
    return [...data].sort((a, b) => sign * compareBy(sortKey, a, b));
  }, [data, sortKey, sortDirection]);

  const selectionIcon = (row: PortfolioPosition) => {
    const isActive = activeAsset?.asset.id === row.asset.id;
    const isDimmed = Boolean(activeAsset) && !isActive;

    return <PerformancePositionSelectionIcon active={isActive} dimmed={isDimmed} />;
  };

  const amountLabel = (row: PortfolioPosition) => `${row.amount} ${row.denomination}`;
  const costBasisLabel = (row: PortfolioPosition) =>
    `${formatDefaultFractionTwo(row.costBasis)} ${row.denomination}`;
  const presentValueLabel = (row: PortfolioPosition) =>
    `${formatDefaultFractionTwo(row.value || 0)} ${row.denomination}`;

  if (isMobile) {
    return (
      <MobileCardList
        items={sortedData.map((row) => ({
          value: row.id,
          title: row.asset.name,
          subtitle: getAssetVisibility(row),
          startIcon: selectionIcon(row),
          content: (
            <>
              <MobileFieldsGrid>
                <MobileCardField label="Amount" value={amountLabel(row)} />
                <MobileCardField label="Cost Basis">
                  <TextCell
                    title={costBasisLabel(row)}
                    caption={row.costBasisInUsd ? formatUsd(row.costBasisInUsd) : undefined}
                  />
                </MobileCardField>
                <MobileCardField label="Present Value">
                  <TextCell
                    title={presentValueLabel(row)}
                    caption={row.valueInUsd ? formatUsd(row.valueInUsd) : undefined}
                  />
                </MobileCardField>
                <MobileCardField label="Visibility">
                  <VisibilityBadge row={row} />
                </MobileCardField>
              </MobileFieldsGrid>
              <MobileRowActions>
                <AssetActions row={row} />
              </MobileRowActions>
            </>
          ),
        }))}
        defaultValue={sortedData[0]?.id ?? null}
        onValueChange={(next) => setActiveAsset(sortedData.find((row) => row.id === next) ?? null)}
        emptyMessage="No portfolio assets."
      />
    );
  }

  const columns: TableColumn<AssetRow>[] = [
    {
      key: 'assetName',
      header: 'Asset Name',
      sortable: true,
      minWidth: 272,
      cell: ({ position }) => (
        <AssetNameCell>
          {selectionIcon(position)}
          <TextCell title={position.asset.name} tooltip={position.asset.name} />
        </AssetNameCell>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      width: '160px',
      cell: ({ position }) => <TextCell title={amountLabel(position)} />,
    },
    {
      key: 'costBasis',
      header: 'Cost Basis',
      sortable: true,
      width: '160px',
      cell: ({ position }) => (
        <TextCell
          title={costBasisLabel(position)}
          caption={position.costBasisInUsd ? formatUsd(position.costBasisInUsd) : undefined}
        />
      ),
    },
    {
      key: 'presentValue',
      header: 'Present Value',
      sortable: true,
      width: '160px',
      cell: ({ position }) => (
        <TextCell
          title={presentValueLabel(position)}
          caption={position.valueInUsd ? formatUsd(position.valueInUsd) : undefined}
        />
      ),
    },
    {
      key: 'visibility',
      header: 'Visibility',
      width: '104px',
      cell: ({ position }) => <VisibilityBadge row={position} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '208px',
      align: 'right',
      pinned: true,
      cell: ({ position }) => (
        <RowActions>
          <AssetActions row={position} />
        </RowActions>
      ),
    },
  ];

  const rows: AssetRow[] = sortedData.map((position) => ({
    key: position.id,
    position,
    onClick: () => setActiveAsset(position === activeAsset ? null : position),
  }));

  return (
    <SelectableTable
      columns={columns}
      rows={rows}
      size="lg"
      sortKey={sortKey}
      sortDirection={sortDirection}
      onSort={handleSort}
      minTableWidth={getTableMinWidth(columns)}
    />
  );
};

export default AssetTable;
